import React, { useEffect, useState } from 'react';
import api from '../api/api';
import {
  OPERATING_GROUP_CHANGED_IN_ANOTHER_TAB_MESSAGE,
  useSelectedOperatingGroup,
} from '../hooks/useSelectedOperatingGroup';
import { KitchenEmployeeDetails, MonthlyRevenue } from '../types';
import { extractApiErrorMessage } from '../utils/apiError';
import { isRestaurantOperatingContext } from '../utils/operatingContext';

interface PersonalPayrollRecord {
  year: number;
  month: number;
  status: 'approved' | 'closed';
  amount: number;
}

const SalaryPage: React.FC = () => {
  const { groupId: selectedGroupId, changedInAnotherTab } = useSelectedOperatingGroup();
  const restaurantContext = isRestaurantOperatingContext(selectedGroupId);
  const allGroupsOverview = selectedGroupId === 'all';
  const [details, setDetails] = useState<KitchenEmployeeDetails | null>(null);
  const [monthlyRevenue, setMonthlyRevenue] = useState(0);
  const [personalHistory, setPersonalHistory] = useState<PersonalPayrollRecord[]>([]);
  const [loading, setLoading] = useState(true);
  const [message, setMessage] = useState('');

  useEffect(() => {
    let active = true;
    const load = async () => {
      setLoading(true);
      setMessage('');
      setDetails(null);
      setPersonalHistory([]);
      setMonthlyRevenue(0);
      if (changedInAnotherTab) {
        setMessage(OPERATING_GROUP_CHANGED_IN_ANOTHER_TAB_MESSAGE);
        setLoading(false);
        return;
      }
      if (allGroupsOverview) {
        setMessage('Особисті нарахування доступні лише в обраному ресторані.');
        setLoading(false);
        return;
      }

      try {
        if (restaurantContext) {
          const response = await api.get<PersonalPayrollRecord[]>('/restaurant-payroll/me/history');
          if (active) setPersonalHistory(response.data);
        } else {
          const now = new Date();
          const year = now.getFullYear();
          const month = now.getMonth() + 1;
          const [detailsRes, revenuesRes] = await Promise.all([
            api.get<KitchenEmployeeDetails>('/kitchen-employees/me'),
            api.get<MonthlyRevenue[]>(`/monthly-revenues?year=${year}`),
          ]);
          if (!active) return;
          setDetails(detailsRes.data);
          setMonthlyRevenue(revenuesRes.data.find((item) =>
            item.year === year && item.month === month)?.amount ?? 0);
        }
      } catch (error: unknown) {
        if (active) setMessage(extractApiErrorMessage(error, 'Не вдалося завантажити дані ЗП.'));
      } finally {
        if (active) setLoading(false);
      }
    };
    void load();
    return () => { active = false; };
  }, [allGroupsOverview, changedInAnotherTab, restaurantContext, selectedGroupId]);

  const fixedSalary = details
    ? details.fixedMonthlyRate + (monthlyRevenue * details.revenuePercent) / 100
    : 0;

  if (restaurantContext || allGroupsOverview) {
    return (
      <main className="page-shell" style={{ maxWidth: '700px' }}>
        <h2 style={{ color: 'var(--crm-text)', borderBottom: '2px solid var(--crm-success)', paddingBottom: '8px' }}>Зарплата</h2>
        {loading ? <p role="status">Завантаження...</p> : null}
        {message && <p role="alert" style={{ color: 'var(--crm-danger)' }}>{message}</p>}
        {!loading && !message && (personalHistory.length ? (
          <div style={{ overflowX: 'auto', marginTop: '16px' }}>
            <table>
              <thead>
                <tr><th>Період</th><th>Статус</th><th>Нараховано</th></tr>
              </thead>
              <tbody>
                {personalHistory.map((record) => (
                  <tr key={`${record.year}-${record.month}`}>
                    <td>{new Date(record.year, record.month - 1, 1).toLocaleDateString('uk-UA', {
                      month: 'long', year: 'numeric',
                    })}</td>
                    <td>{record.status === 'approved' ? 'Погоджено' : 'Закрито'}</td>
                    <td>{record.amount.toFixed(2)} ₴</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : <p>Немає затверджених нарахувань у цьому ресторані.</p>)}
      </main>
    );
  }

  return (
    <div style={{ padding: '24px', maxWidth: '700px', margin: '0 auto' }}>
      <h2 style={{ color: 'var(--crm-text)', borderBottom: '2px solid var(--crm-success)', paddingBottom: '8px' }}>Зарплата</h2>
      {message && <p style={{ color: 'var(--crm-danger)' }}>{message}</p>}
      {loading ? (
        <p>Завантаження...</p>
      ) : (
        details && (
          <div style={{ background: 'var(--crm-surface)', border: '1px solid var(--crm-border)', color: 'var(--crm-text)', padding: '24px', borderRadius: 'var(--crm-radius-md)', boxShadow: 'var(--crm-shadow-sm)', marginTop: '16px' }}>
            <p><strong>Працівник:</strong> {details.fullName}</p>
            <p><strong>Режим ЗП:</strong> {details.useFixedRateSalary ? 'Ставка + % від виторгу' : 'За коефіцієнтом'}</p>
            {details.useFixedRateSalary ? (
              <>
                <p><strong>Ставка:</strong> {details.fixedMonthlyRate.toFixed(2)} ₴</p>
                <p><strong>Відсоток:</strong> {details.revenuePercent.toFixed(2)} %</p>
                <p><strong>Виторг за місяць:</strong> {monthlyRevenue.toFixed(2)} ₴</p>
                <p><strong>Нарахована ЗП:</strong> <span style={{ color: 'var(--crm-success)', fontWeight: 700 }}>{fixedSalary.toFixed(2)} ₴</span></p>
              </>
            ) : (
              <>
                <p><strong>Підсумковий коефіцієнт:</strong> {details.summaryCoefficient.toFixed(4)}</p>
                <p><strong>Відпрацьовано годин:</strong> {details.totalHoursWorked.toFixed(2)}</p>
                <p><strong>Зважені години:</strong> {details.totalWeightedHours.toFixed(2)}</p>
                <p style={{ color: 'var(--crm-text-muted)' }}>Нарахування по коефіцієнту залишається без змін, як і просили.</p>
              </>
            )}
          </div>
        )
      )}
    </div>
  );
};

export default SalaryPage;
